/**
 * Utility functions to handle image URLs from different sources
 * with appropriate resizing parameters
 */

import { ImageTypeDto } from 'client/types';

// Domain and URL constants
const GOOGLE_MAPS_DOMAIN = 'maps.googleapis.com';
const MEDIA_STATIC_DOMAIN = 'media.staticontent.com';
const GOOGLE_PLACES_API_URL = 'https://maps.googleapis.com/maps/api/place/photo';
const MEDIA_PICTURES_URL = 'https://media.staticontent.com/media/pictures/';

/**
 * Generates a thumbnail URL (120x75) based on the image source
 */
export const getThumbnailUrl = (url: string): string => {
  return buildImageUrl({ image: url }, 120, 75);
};

/**
 * Generates a full-size URL based on the image source
 * This function can be used to get higher resolution images when available
 */
export const getFullSizeUrl = (url: string): string => {
  return buildImageUrl({ image: url }, 1600);
};

/**
 * Builds image URLs from ImageTypeDto objects or plain URLs with optional dimensions
 * Supports automatic type inference from URL domain when type is not specified
 */
export function buildImageUrl(image: ImageTypeDto | { image: string }, width?: number, height?: number): string {
  const apiKey = process.env.GOOGLE_PLACES_API_KEY;

  let type: string;
  let imageValue: string;

  // If no explicit type, infer from domain
  if (!('type' in image)) {
    const url = image.image;

    if (url.includes(GOOGLE_MAPS_DOMAIN)) {
      type = 'GOOGLE_ID';
      imageValue = url;
    } else if (url.includes(MEDIA_STATIC_DOMAIN)) {
      type = 'ID';
      imageValue = url;
    } else {
      type = 'URL';
      imageValue = url;
    }
  } else {
    type = image.type;
    imageValue = image.image;
  }

  switch (type) {
    case 'GOOGLE_ID': {
      // Use 900px width default if no dimensions provided (Google API requires at least one parameter)
      const finalWidth = width || (!width && !height ? 900 : undefined);

      const urlParams = new URLSearchParams(imageValue.split('?')[1]);
      const photoRef = urlParams.get('photoreference') || urlParams.get('photo_reference') || imageValue;
      let url = `${GOOGLE_PLACES_API_URL}?photoreference=${photoRef}&key=${apiKey}`;

      if (finalWidth) {
        url += `&maxwidth=${finalWidth}`;
      }

      if (height) {
        url += `&maxheight=${height}`;
      }

      return url;
    }

    case 'ID': {
      // We're defaulting the url expecting to be the full image url
      let url = imageValue;

      // If imageValue is only the uuid
      if (!imageValue.includes(MEDIA_STATIC_DOMAIN)) {
        url = MEDIA_PICTURES_URL + imageValue;
      }

      if (width && height) {
        url += `/${width}x${height}?op=TRUNCATE&enlarge=false`;
      }

      return url;
    }

    case 'URL':
    default:
      // For direct URLs, return as-is (no dimension processing)
      return imageValue;
  }
}
